import React, { useCallback, useEffect, useRef } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';

import {
    Button,
    leftNavConstants,
    LeftNavHeader,
    LoadingSpinner,
} from '@trqk/components';
import { useAutoSize } from '@trqk/hooks';
import { locale, getURLSearchParams, updateURLSearchParams } from '@trqk/utils';

import { WebLayout } from '../../templates';
import {
    useGapAnalysisTools,
    useModal,
    usePaymentPlanStatus,
    useEntities,
    useStatementSelection,
} from '../../../hooks';

import {
    GapAnalysisExplainer,
    GapAnalysisReportSummaryTable,
} from './components';
import { FULL_PATHS, MODAL_IDS, PATHS, URL_PARAMS } from '../../../constants';
import { heights } from './constants';
import { ViewGapAnalysis, ManageGapAnalysis } from '../../modals';
import { ShouldUploadStatements } from '../../emptyStates';

const { AUTO_SIZE_OFFSETS, HEIGHTS, WIDTHS } = leftNavConstants;

/**
 * Shows a list of past Gap Analysis reports with various links for downloading each report. Also
 * renders a button that allows the user to open a modal to guide them through the process of
 * creating a Gap Analysis report.
 * @returns JSX
 */
export const GapAnalysis = (): JSX.Element => {
    const {
        fetchGapSummaries,
        gapSummaries,
        gapSummariesRefreshTime,
        openManageGapAnalysis,
        startGapAnalysisEdit,
        send,
    } = useGapAnalysisTools();
    const history = useHistory();
    const { noSelectedEntity } = useEntities();
    const {
        statementSelectionProps: { hasStatements },
    } = useStatementSelection();
    const { add: addModal } = useModal();
    const manageGapAnalysisRouteMatch = useRouteMatch({
        path: FULL_PATHS.MANAGE_GAP_ANALYSIS,
        strict: true,
        sensitive: true,
    });

    const manageModalOpenRef = useRef(false);

    const handleCloseManageGapAnalysis = useCallback(() => {
        // Update the list of report summaries so GapAnalysisReportSummaryTable includes any newly
        // created report
        fetchGapSummaries();

        send({
            type: 'RESTORE_CONTEXT',
        });

        const nextSearch = updateURLSearchParams(history.location.search, [
            {
                key: URL_PARAMS.GAP_ANALYSIS_ID,
                value: null,
            },
            {
                key: URL_PARAMS.GAP_ANALYSIS_MODE,
                value: null,
            },
        ]);

        history.push({
            pathname: PATHS.GAP_ANALYSIS,
            search: nextSearch,
        });

        // This ref update has to happen after history.push() to get the desired effect - otherwise,
        // nothing happens when the user tries to click an Edit button after the modal has
        // previously been closed.
        manageModalOpenRef.current = false;
    }, [fetchGapSummaries, manageModalOpenRef.current]);

    const hasManageGapAnalysisRouteMatch = manageGapAnalysisRouteMatch !== null;

    // Ensure the ManageGapAnalysis modal is open if the route matches `/gap-analysis/manage`
    useEffect((): void => {
        if (
            hasManageGapAnalysisRouteMatch &&
            manageModalOpenRef.current === false
        ) {
            manageModalOpenRef.current = true;

            const params = getURLSearchParams({
                configs: [
                    {
                        paramKey: URL_PARAMS.GAP_ANALYSIS_ID,
                        defaultValue: null,
                    },
                    {
                        paramKey: URL_PARAMS.GAP_ANALYSIS_MODE,
                        defaultValue: null,
                    },
                ],
                search: history.location.search,
            });

            const gapAnalysisIdParam = params[URL_PARAMS.GAP_ANALYSIS_ID];
            const gapAnalysisModeParam = params[URL_PARAMS.GAP_ANALYSIS_MODE];

            // If the URL has params for the gap_analysis_id and "edit" mode, fetch the data for the
            // trqkGap and channels
            if (gapAnalysisIdParam && gapAnalysisModeParam === 'edit') {
                startGapAnalysisEdit(gapAnalysisIdParam);
            }

            addModal({
                Content: ManageGapAnalysis,
                handleClose: handleCloseManageGapAnalysis,
                id: MODAL_IDS.MANAGE_GAP_ANALYSIS,
            });
        }
    }, [
        handleCloseManageGapAnalysis,
        history.location.search,
        hasManageGapAnalysisRouteMatch,
        manageModalOpenRef.current,
        startGapAnalysisEdit,
        addModal,
    ]);

    const viewGapAnalysisRouteMatch = useRouteMatch({
        path: FULL_PATHS.VIEW_GAP_ANALYSIS,
        strict: true,
        sensitive: true,
    });

    const viewModalOpenRef = useRef(false);

    const handleCloseViewGapAnalysis = useCallback(() => {
        const restoreSearch = updateURLSearchParams(history.location.search, [
            {
                key: URL_PARAMS.GAP_ANALYSIS_ID,
                value: null,
            },
        ]);

        history.push({
            search: restoreSearch,
            pathname: PATHS.GAP_ANALYSIS,
        });

        viewModalOpenRef.current = false;
    }, [history.push, history.location.search, viewModalOpenRef.current]);

    const hasViewGapAnalysisRouteMatch = viewGapAnalysisRouteMatch !== null;

    // Ensure the ViewGapAnalysis modal is open if the route matches `/gap-analysis/view`
    useEffect((): void => {
        if (
            hasViewGapAnalysisRouteMatch &&
            viewModalOpenRef.current === false
        ) {
            // Use gap_analysis_id from URL parameters to open ViewGapAnalysis
            const params = getURLSearchParams({
                configs: [
                    {
                        paramKey: URL_PARAMS.GAP_ANALYSIS_ID,
                        defaultValue: null,
                    },
                ],
                search: history.location.search,
            });

            const gapAnalysisIdFromParams = params[URL_PARAMS.GAP_ANALYSIS_ID];

            if (gapAnalysisIdFromParams) {
                addModal({
                    Content: ViewGapAnalysis,
                    input: {
                        gapAnalysisId: gapAnalysisIdFromParams,
                    },
                    handleClose: handleCloseViewGapAnalysis,
                    id: MODAL_IDS.VIEW_GAP_ANALYSIS,
                });

                viewModalOpenRef.current = true;
            } else {
                // If the URL param for gap_analysis_id is missing, do not open the modal; instead,
                // replace the current path with '/gap-analysis'
                history.replace({
                    search: history.location.search,
                    pathname: PATHS.GAP_ANALYSIS,
                });
            }
        }
    }, [
        hasViewGapAnalysisRouteMatch,
        history.location.search,
        history.replace,
        handleCloseViewGapAnalysis,
        viewModalOpenRef.current,
    ]);

    const contentAreaRef = useRef<HTMLDivElement>(null);
    const { showFreeTrialFooter, isPublisherEdition } = usePaymentPlanStatus();
    const { height, width } = useAutoSize({
        elementRef: contentAreaRef,
        resizeRate: 400,
        rightOffset: AUTO_SIZE_OFFSETS.RIGHT,
        bottomOffset: showFreeTrialFooter
            ? HEIGHTS.ACCOUNT_DROPDOWN + heights.autoSizeOffsetBottom
            : heights.autoSizeOffsetBottom,
        minHeight: heights.minSectionHeight,
        minWidth: WIDTHS.MAIN_XL - AUTO_SIZE_OFFSETS.MIN_WIDTH,
    });

    const mainWidth = Math.max(WIDTHS.MAIN_XL, width);

    const showLoadingSpinner =
        !noSelectedEntity &&
        (gapSummariesRefreshTime === 0 || isPublisherEdition === null);
    const shouldUploadStatements = noSelectedEntity && !hasStatements;

    return (
        <WebLayout
            headerPadding="xs"
            header={{
                content: (
                    <LeftNavHeader>
                        <div
                            className="flex justify-between items-start"
                            style={{ minWidth: 760 }}
                        >
                            <h3 data-cy="gap-analysis-title">
                                {locale.enUS.GAP_ANALYSIS.TITLE}
                            </h3>
                            <div
                                style={{
                                    marginRight: 360,
                                }}
                            >
                                {isPublisherEdition && (
                                    <Button
                                        autoWidth={true}
                                        padding="lg"
                                        color="primary-light-bg"
                                        handleClick={() => {
                                            send({
                                                type: 'SET_LOADING',
                                                value: {
                                                    loading: true,
                                                },
                                            });

                                            openManageGapAnalysis({
                                                mode: 'create',
                                            });
                                        }}
                                    >
                                        <p className="flex justify-center items-center text-sm font-semibold px-2">
                                            {
                                                locale.enUS.GAP_ANALYSIS
                                                    .MANAGE_GAP_ANALYSIS
                                            }
                                        </p>
                                    </Button>
                                )}
                            </div>
                        </div>
                        <div className="mt-3 text-sm">
                            {locale.enUS.GAP_ANALYSIS.DESCRIPTION.PART_1}
                            <br />
                            {locale.enUS.GAP_ANALYSIS.DESCRIPTION.PART_2}
                        </div>
                    </LeftNavHeader>
                ),
            }}
            mainWidth={mainWidth}
        >
            <div ref={contentAreaRef}>
                {isPublisherEdition && (
                    <>
                        {shouldUploadStatements && <ShouldUploadStatements />}
                        {!shouldUploadStatements && (
                            <div
                                className="bg-white rounded-lg p-8 mt-6"
                                style={{
                                    width: mainWidth,
                                }}
                            >
                                {showLoadingSpinner && (
                                    <div
                                        className="flex justify-center items-center"
                                        style={{ height }}
                                    >
                                        <LoadingSpinner />
                                    </div>
                                )}
                                {!showLoadingSpinner && (
                                    <GapAnalysisReportSummaryTable
                                        height={height}
                                        trqkGapSummaries={gapSummaries}
                                        width={mainWidth - 80}
                                    />
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
            {isPublisherEdition === false && <GapAnalysisExplainer />}
        </WebLayout>
    );
};
